import { Component, Host, h, Element, Prop, Watch } from "@stencil/core";

import { ShareService } from "../../index";
import { StzhShareLocalizedText } from "./stzh-share.localization";
import { getMeta } from "../../utils/utils";
import { abbreviateText } from "../../utils/string-utils";

// 280 - URL (always 24) = 256
const MAX_TWEET_LENGTH = 256;

/**
 */
@Component({
  tag: "stzh-share",
  styleUrl: "stzh-share.scss",
  scoped: true,
})
export class StzhShare {
  /** Translation strings */
  @Prop() localization: StzhShareLocalizedText;

  /** URL of current site (uses full URL by default) */
  @Prop() url: string = window.location.href;

  /** Overwrite mail subject translation */
  @Prop() mailSubject: string;

  /** Overwrite mail body translation */
  @Prop() mailBody: string;

  /**
   * Array of enabled share buttons.
   * Possible services: `facebook` / `twitter` / `linkedin` / `xing` / `mail`
   */
  @Prop() services: ShareService[] | string = ["mail", "facebook", "twitter", "linkedin", "xing"];

  private _services: ShareService[] = [];

  @Watch("services")
  servicesWatcher(newValue: ShareService[] | string) {
    if (typeof newValue === "string") {
      this._services = JSON.parse(newValue);
    } else {
      this._services = newValue;
    }
  }

  @Element() element: HTMLStzhShareElement;

  private openPopup = (url, event) => {
    event.preventDefault();
    window.open(url, "_blank", "width=600,height=460");
  };

  async componentWillLoad() {
    this.servicesWatcher(this.services);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "share");
    }
  }

  render() {
    const classes = {
      "stzh-share": true,
    };

    const shareUrl = encodeURIComponent(this.url);
    const mailSubject = encodeURIComponent(this.mailSubject || this.localization.mailSubject);
    const mailBody = encodeURIComponent(
      (this.mailBody || this.localization.mailBody)?.replace(/\{shareUrl\}/gi, this.url),
    );

    const title = encodeURIComponent(document.title);
    const twitterTitle = encodeURIComponent(abbreviateText(title, MAX_TWEET_LENGTH));
    const description = encodeURIComponent(getMeta("description"));

    const serviceData = {
      mail: {
        popup: false,
        blank: false,
        url: `mailto:?subject=${mailSubject}&body=${mailBody}`,
        text: this.localization.shareMail,
        icon: "mail",
      },
      facebook: {
        popup: true,
        blank: true,
        url: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
        text: this.localization.shareFacebook,
        icon: "facebook",
      },
      twitter: {
        popup: true,
        blank: true,
        url: `https://twitter.com/intent/tweet?text=${twitterTitle}&url=${shareUrl}`,
        text: this.localization.shareTwitter,
        icon: "twitter",
      },
      linkedin: {
        popup: true,
        blank: true,
        url: `https://www.linkedin.com/shareArticle?mini=true&summary=${description}&title=${title}&url=${shareUrl}`,
        text: this.localization.shareLinkedin,
        icon: "linkedin",
      },
      xing: {
        popup: true,
        blank: true,
        url: `https://www.xing.com/spi/shares/new?url=${shareUrl}`,
        text: this.localization.shareXing,
        icon: "xing",
      },
    };

    return (
      <Host>
        <div class={classes}>
          <ul class="stzh-share__list">
            {this._services.map(service => (
              <li class="stzh-share__item">
                <stzh-tooltip content={serviceData[service].text} size="small">
                  <stzh-button
                    class={`stzh-share__button is-${service}`}
                    size="small"
                    variant="tertiary"
                    iconOnly={true}
                    href={serviceData[service].url}
                    rel={serviceData[service].blank ? "nofollow noopener noreferrer" : "nofollow"}
                    target={serviceData[service].blank ? "_blank" : "_self"}
                    onClick={serviceData[service].popup ? e => this.openPopup(serviceData[service].url, e) : null}
                    analyticsId={serviceData[service].text}
                    icon={serviceData[service].icon}
                  ></stzh-button>
                </stzh-tooltip>
              </li>
            ))}
          </ul>
        </div>
      </Host>
    );
  }
}
